import axios from "axios";

import { API_URL } from "./env";
import { getLocalData } from "./LocalStorage";
import { AUTH_TOKEN_TEXT } from "../Constants";

const setHeaders = (tempToken = "") => {
  let token;
  if (!tempToken) {
    token = getLocalData(AUTH_TOKEN_TEXT);
  } else {
    token = tempToken;
  }

  if (token) {
    axios.defaults.headers.common.authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.authorization;
  }
};

const postData = async (url, data) => {
  setHeaders();

  console.log("URL :", url, data);

  const res = await axios
    .post(url, data, {
      headers: { "Content-Type": "application/json" },
    })
    .then((res) => res.data)
    .catch((err) => {
      return { status: err.status, ...err.response?.data };
    });

  return res;
};

const postMultipart = async (url, formData) => {
  setHeaders();
  return axios
    .post(url, formData)
    .then((res) => res.data)
    .catch((err) => ({ status: err.status, ...err.response?.data }));
};

const otherRequest = async (method, url, data) => {
  await setHeaders();

  return axios({
    method,
    url: url,
    data,
    config: {
      headers: {
        "Content-Type": "application/json",
      },
    },
  })
    .then((res) => res.data)
    .catch((err) => {
      return { status: err.status, ...err.response?.data };
    });
};

const getData = async (url, params = {}) => {
  setHeaders();

  let newUrl = url;

  if (Object.entries(params).length > 0) {
    const queryParams = new URLSearchParams(params).toString();
    newUrl = queryParams ? `${newUrl}?${queryParams}` : newUrl;
  }

  return axios(newUrl)
    .then((res) => res.data)
    .catch((err) => {
      return { status: err.status, ...err.response?.data };
    });
};

export const API = {
  login: (data) => postData(`${API_URL}/user/login`, data),
  signup: (data) => postData(`${API_URL}/user/signup`, data),
  verfiyEmail: (data) => postData(`${API_URL}/user/verify-email`, data),
  forgotPasswordRequest: (data) =>
    postData(`${API_URL}/user/forgot-password`, data),
  verifyForgotOtpRequest: (data) =>
    postData(`${API_URL}/user/password-reset-otp`, data),
  updatePasswordRequest: (data) =>
    postData(`${API_URL}/user/reset-password`, data),
  resendOtp: (data) => postData(`${API_URL}/user/resend-password-otp`, data),
  resendSignupOtp: (data) =>
    postData(`${API_URL}/user/resend-verification-otp`, data),

  getAuthUser: () => getData(`${API_URL}/user/profile`),
  updateProfile: (data) => postData(`${API_URL}/user/update-profile`, data),
  deleteAccount: (data) => postData(`${API_URL}/user/delete-user`, data),

  // App version info (used by mobile cold-start update prompts; admin-writable)
  getAppVersionInfo: () => getData(`${API_URL}/app/version-info`),
  updateAppVersionInfo: (data) =>
    postData(`${API_URL}/app/version-info`, data),

  getSingleSpot: (spotId) => getData(`${API_URL}/spot/single/${spotId}`),
  getSinglePayment: (spotId) => getData(`${API_URL}/spot/single/${spotId}`),
  getDateSlots: (data) => getData(`${API_URL}/availability/date-slots`, data),
  getUnavailableDate: (spotId, data) =>
    getData(`${API_URL}/spot/booked-dates/${spotId}`, data),

  // Users
  getUserSpots: (data) => getData(`${API_URL}/spot/user-spots`, data),
  deleteUserSpot: (data) => postData(`${API_URL}/spot/delete`, data),
  createSpot: (formData) => postMultipart(`${API_URL}/spot/create`, formData),
  updateSpot: (formData) => postMultipart(`${API_URL}/spot/update`, formData),
  // Unauthenticated lead capture from the /dc and /nyc landing pages.
  // FormData carries name + email plus the standard spot fields so the
  // server can create the host account and the spot in one call.
  claimSpot: (formData) => postMultipart(`${API_URL}/spot/claim`, formData),
  // Step-1 email gate for the landing wizard. Server checks if the email
  // has an account and creates an inactive one if missing. When exists is
  // true the server also sends an OTP so the frontend can open the verify
  // modal without a separate send call.
  leadCheck: (data) => postData(`${API_URL}/user/lead-check`, data),
  // Step-1 verify for returning users. Returns { accessToken, user,
  // draftSpot } — draftSpot may be null if the user has no spot in flight.
  verifyOtpLead: (data) => postData(`${API_URL}/user/verify-otp-lead`, data),
  // Step-2 upsert. Multipart FormData with the details+images payload
  // (no availability). Returns the (draft) spot + a fresh accessToken so
  // the brand-new user is authenticated for step 3.
  saveDraftSpot: (formData) =>
    postMultipart(`${API_URL}/spot/save-draft`, formData),
  // Step-3 finalize. JSON body with the availability payload; server
  // attaches it to the draft spot and flips status → active.
  activateSpot: (data) => postData(`${API_URL}/spot/activate`, data),
  createAvailability: (data) =>
    postData(`${API_URL}/availability/create`, data),
  updateAvailability: (data) =>
    postData(`${API_URL}/availability/update`, data),
  deleteAvailability: (data) =>
    postData(`${API_URL}/availability/delete`, data),
  addDateAvailability: (data) =>
    postData(`${API_URL}/date-availability/add`, data),
  updateDateAvailability: (data) =>
    postData(`${API_URL}/date-availability/update`, data),
  deleteDateAvailability: (data) =>
    postData(`${API_URL}/date-availability/delete`, data),
  getUserVehicles: (data) => getData(`${API_URL}/vehicle/user-vehicles`, data),
  createVehicle: (data) => postData(`${API_URL}/vehicle/create`, data),
  updateVehicle: (data) => postData(`${API_URL}/vehicle/update`, data),
  deleteUserVehicle: (data) => postData(`${API_URL}/vehicle/delete`, data),
  getAdminBookings: (data) => getData(`${API_URL}/booking/query`, data),
  deleteAdminBooking: (data) => postData(`${API_URL}/booking/cancel`, data),
  getAdminPayments: (data) => getData(`${API_URL}/payment/query`, data),
  getAdminUserList: (data) => getData(`${API_URL}/user/query`, data),
  updateUserStatus: (data) => postData(`${API_URL}/user/block-unblock`, data),
  deleteAdminUser: (id) => postData(`${API_URL}/user/delete`, id),

  // Admin
  getAdminSpots: (data) => getData(`${API_URL}/spot/query`, data),
  deleteAdminSpot: (data) => postData(`${API_URL}/spot/delete`, data),
  getAdminVehicles: (data) => getData(`${API_URL}/vehicle/query`, data),
  deleteAdminVehicle: (data) => postData(`${API_URL}/vehicle/delete`, data),
  getUserBookings: (data) => getData(`${API_URL}/booking/client-bookings`, data),
  getUserPastBookings: (data) =>
    getData(`${API_URL}/booking/client-past-bookings`, data),
  getHostBookings: (data) => getData(`${API_URL}/booking/host-bookings`, data),
  deleteUserBooking: (data) => postData(`${API_URL}/booking/cancel`, data),
  getUserPayments: (data) => getData(`${API_URL}/payment/host`, data),

  // Booking
  createBooking: (data) => postData(`${API_URL}/booking/book`, data),
  getSingleBooking: (boookingId) =>
    getData(`${API_URL}/booking/single/${boookingId}`),
  confirmPayment: (data) => postData(`${API_URL}/payment/confirm`, data),
  failedBookingPayment: (data) => postData(`${API_URL}/payment/confirm`, data),
  refreshPaymentIntent: (data) =>
    postData(`${API_URL}/payment/refresh`, data),
  getPublicKeys: (data) => postData(`${API_URL}/payment/public-key`, data),

  // Host requests (combined booking + time-change + payment-pending)
  getCombinedRequests: (data) =>
    getData(`${API_URL}/booking/combined-booking-timechange`, data),
  acceptBookingRequest: (data) =>
    postData(`${API_URL}/booking/accept-request`, data),
  denyBookingRequest: (data) =>
    postData(`${API_URL}/booking/deny-request`, data),
  acceptBookingTimechange: (data) =>
    postData(`${API_URL}/booking/accept-timechange`, data),
  denyBookingTimechange: (data) =>
    postData(`${API_URL}/booking/deny-timechange`, data),

  // Booking check-in / check-out
  vehicleCheckIn: (data) =>
    postData(`${API_URL}/booking-log/user-checkin`, data),
  vehicleCheckOut: (data) =>
    postData(`${API_URL}/booking-log/user-checkout`, data),
  hostVehicleCheckIn: (data) =>
    postData(`${API_URL}/booking-log/host-checkin`, data),
  hostVehicleCheckOut: (data) =>
    postData(`${API_URL}/booking-log/host-checkout`, data),
};
